/* eslint-disable complexity */
import {
  PLANTING,
  IN_SEASON,
  PRECISION_PLANTING_SUB_LAYERS,
} from '../constants/mapLayerIds';
import {
  IMPORTED_SHAPEFILES_FEATURE_FLAG,
  OTHER_IMAGERY_FEATURE_FLAG,
  CROP_WATER_FEATURE_FLAG,
  PRECISION_PLANTING_MAPS_FEATURE_FLAG,
  DMB_TARGET_POPULATION_FEATURE_FLAG,
  PRESCRIPTION_MAPS_WEB_FEATURE_FLAG,
  DMB_WEB_AG_VIZ,
} from '../constants/featureFlags';
import {
  LOCALE_GROUPS,
  POPULATION_WEIGHT_UNITS,
  FVP_APPLICATION_DRY_UNITS,
} from '../constants/units';
import {
  RECEIVE_FEATURES,
} from '../constants/actionTypes';

import { LegendAction, LegendActions } from '../actions/legendActions';
import { AgVizAction, AgVizActions } from '../actions/agVizActions';
import { transformAgVizMapsForState } from '../utils/agVizTransformer';
import { uapiLayerNameToDmbLayerId } from '../utils/agVizLayerMapper';
import { getSeasonNameFromType } from '../utils/seasonUtils';
import { SeasonTypeCode } from '../types/Season';
import { FeatureActions, FeatureFlagResponse, ReceiveFeaturesAction } from '../actions/featureActions';
import { NamedEntity } from '../types/Common';
import { FhaSubLayer, LayerList, SeasonCodeId } from '../types';

import { getCurrLayer, isLayerSeasonAgnostic } from '../utils/layerData';
import { SEASON_AGNOSTIC } from '../constants/seasonIds';

export interface AgVizLayersState {
  activeMapLayers: NamedEntity[];
  isSplitViewEnabled: boolean;
  isFhaTimelapsePlaying: boolean;
  list: LayerList;
  agVizAlreadyEnabled: boolean;
  disabledMapLayers: string[];
}

export const initialState: AgVizLayersState = {
  activeMapLayers: [{ id: '', name: '' }],
  disabledMapLayers: [],
  isSplitViewEnabled: false,
  isFhaTimelapsePlaying: false,
  list: {},
  agVizAlreadyEnabled: false,
};


interface StructureLayerValueParams {
  list: LayerList;
  layer: string;
  newValues: Record<string, unknown> | FhaSubLayer;
  season?: string;
  subLayer?: string;
}
/**
 * When adding new values into a layer object, sublayers must be checked. If a sublayer is defined,
 * new values should be nested within it. Otherwise, new values can be returned as-is.
 */
function structureLayerValue({
  list, layer, newValues, season, subLayer
}: StructureLayerValueParams) {
  if (subLayer) {
    const currLayer = getCurrLayer(list, layer, season);
    return {
      subLayers: {
        ...currLayer.subLayers,
        [subLayer]: { ...currLayer.subLayers[subLayer], ...newValues },
      },
    };
  }

  return newValues;
}

function isAgVizFeatureFlagEnabled(flagValue: FeatureFlagResponse): boolean {
  return typeof flagValue === 'object' && flagValue !== null && 'value' in flagValue ?
    !!flagValue.value :
    !!flagValue;
}


function isAgVizFeatureFlagenabledInAction(action: ReceiveFeaturesAction): boolean {
  let agVizFeatureFlag = false;
  const agVizFeatureFlagIndex = action.keys.indexOf(DMB_WEB_AG_VIZ);
  if (agVizFeatureFlagIndex >= 0) {
    agVizFeatureFlag = isAgVizFeatureFlagEnabled(action.values[agVizFeatureFlagIndex])
  }

  return agVizFeatureFlag;
}


export interface UpdateLayerValuesParams extends Omit<StructureLayerValueParams, 'list'> {
  state: AgVizLayersState;
}

export const untypedUpdateLayerValues = (
  state: AgVizLayersState,
  layer: string,
  newValues: LayerList,
  season?: string,
  subLayer?: string,
) => updateLayerValues({ state, layer, newValues, season, subLayer });

/**
 * Return a new state object for this reducer, given a specific layer and new values
 * to add to the layer. The layer can be season-specific or season-agnostic.
 */
function updateLayerValues({
  state, layer, newValues, season, subLayer
}: UpdateLayerValuesParams): AgVizLayersState {
  const layerValues = structureLayerValue({
    list: state.list,
    layer,
    newValues,
    season,
    subLayer
  });

  const seasonId: SeasonCodeId = isLayerSeasonAgnostic(layer)
    ? SEASON_AGNOSTIC : (season || '');

  return {
    ...state,
    list: {
      ...state.list,
      [seasonId]: {
        ...state.list[seasonId],
        [layer]: {
          ...state.list?.[seasonId]?.[layer],
          ...layerValues,
        },
      },
    },
  };
}

export function agVizLayersReducer(state: AgVizLayersState = initialState, action: LegendActions | AgVizActions | FeatureActions) {
  // NOSONAR
  switch (action.type) {
    case LegendAction.ReceiveLegend: {
      // Legacy Legends
      const { layer, legend, season, subLayer } = action;
      if (!legend.length) {
        return updateLayerValues({ state, layer, newValues: { legend }, season, subLayer });
      }

      let localeGroup = {};
      const validLayers = [PLANTING.POPULATION, PLANTING.TARGET_POPULATION];
      if (
        validLayers.includes(layer) &&
        legend[0].units === POPULATION_WEIGHT_UNITS.FVP
      ) {
        localeGroup = {
          localeGroup: LOCALE_GROUPS.POPULATION_WEIGHT,
        };
      } else if (layer === IN_SEASON.APPLICATIONS) {
        localeGroup = {
          localeGroup:
            legend[0].units === FVP_APPLICATION_DRY_UNITS
              ? LOCALE_GROUPS.APPLICATION_DRY
              : LOCALE_GROUPS.APPLICATION_LIQUID,
        };
      }
      const newLegend = { legend, ...localeGroup };
      return updateLayerValues({state, layer, newValues: newLegend, season, subLayer});
    }

    case RECEIVE_FEATURES: {
      // This case is called multiple times:
      // - Once for legacy feature flag
      // - Once for LaunchDarkly feature flags
      //
      // We need to check if the Ag-Viz feature flag is enabled to
      // determine to re-enable these layers and clean up the code.
      // Many of these layers are being generated by ARD so they are available.
      const agVizFeatureFlag = isAgVizFeatureFlagenabledInAction(action);

      if(agVizFeatureFlag) {
        const enableLayers = [
          IN_SEASON.IMPORTED_SHAPEFILE,
          IN_SEASON.OTHER_IMAGERY,
          IN_SEASON.CROP_WATER,
          ...Object.values(PRECISION_PLANTING_SUB_LAYERS),
          PLANTING.TARGET_POPULATION,
        ];

        return {
          ...state,
          disabledMapLayers: [...state.disabledMapLayers.filter(layerId => !enableLayers.includes(layerId))],
          agVizAlreadyEnabled: true,
        };
      }

      if (state.agVizAlreadyEnabled) {
        return state;
      }

      const disabledMapLayers = action.keys.reduce(
        (_disabledMapLayers, key, index) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          if (!action.values[index].value || agVizFeatureFlag) {
            if (key === IMPORTED_SHAPEFILES_FEATURE_FLAG) {
              return _disabledMapLayers.concat(IN_SEASON.IMPORTED_SHAPEFILE);
            }

            if (key === OTHER_IMAGERY_FEATURE_FLAG) {
              return _disabledMapLayers.concat(IN_SEASON.OTHER_IMAGERY);
            }

            if (key === CROP_WATER_FEATURE_FLAG) {
              return _disabledMapLayers.concat(IN_SEASON.CROP_WATER);
            }

            if (key === PRECISION_PLANTING_MAPS_FEATURE_FLAG) {
              return _disabledMapLayers.concat(
                Object.values(PRECISION_PLANTING_SUB_LAYERS)
              );
            }

            if (key === DMB_TARGET_POPULATION_FEATURE_FLAG) {
              return _disabledMapLayers.concat(PLANTING.TARGET_POPULATION);
            }

            if (key === PRESCRIPTION_MAPS_WEB_FEATURE_FLAG) {
              return _disabledMapLayers.concat(
                PLANTING.PLANTING_SCRIPT,
                IN_SEASON.APPLICATION_SCRIPT,
                IN_SEASON.FERTILITY_SCRIPT
              );
            }
          }

          return _disabledMapLayers;
        },
        []
      );

      return {
        ...state,
        disabledMapLayers: [...state.disabledMapLayers, ...disabledMapLayers],
      };
    }
    case AgVizAction.ReceiveAgVizMaps: {
      const { maps, cropCode, seasonName, seasonYear } = action;
      
      // Transform UAPI maps to DMB format
      const transformedMaps = transformAgVizMapsForState(maps);
      
      // Find the actual season ID that matches this year/crop/season combination
      // Need to match against existing seasons in state to get the correct region:position format
      // Note: seasonName from action is full name (SPRING), season code in state is abbreviated (SPR)
      const matchingSeasonId = Object.keys(state.list || {}).find((sid) => {
        const parts = sid.split(':');
        if (parts.length < 5) return false;
        const [year, crop, , , seasonCode] = parts;
        // Convert season code (SPR, WIN, etc.) to full name (SPRING, WINTER, etc.) for comparison

        const fullSeasonName = getSeasonNameFromType((seasonCode as unknown) as SeasonTypeCode);
        return (
          parseInt(year, 10) === seasonYear &&
          crop === cropCode &&
          fullSeasonName === seasonName
        );
      });
      
      if (!matchingSeasonId) {
        return state;
      }
      
      // Update each layer with its tile URL and legend
      let newState = state;
      Object.entries(transformedMaps).forEach(([uapiLayerName, layerData]) => {
        // Convert UAPI layer name to DMB layer ID
        const dmbLayerId = uapiLayerNameToDmbLayerId(uapiLayerName);
        
        // Store Ag-Viz data under the matched seasonId and DMB layer ID
        // This makes it compatible with existing component structure:
        // state.layers.list[activeSeason.id][activeMapLayer.id]

        const localeGroup = layerData.localeGroup ? {
          localeGroup: layerData.localeGroup,
        } : {};
        
        const layerValues = {
          legend: layerData.intervals,
          agVizTileUrl: layerData.tileUrlTemplate,
          agVizThumbnailUrl: layerData.thumbnailUrl,
          agVizUnits: layerData.units,
          agVizVersion: layerData.version,
          ...localeGroup,
        };
        
        // Store in state.layers.list[matchingSeasonId][dmbLayerId]
        newState = {
          ...newState,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          list: {
            ...newState.list,
            [matchingSeasonId]: {
              ...newState.list[matchingSeasonId],
              [dmbLayerId]: {
                ...newState.list[matchingSeasonId]?.[dmbLayerId],
                ...layerValues,
              },
            },
          },
        };
      });
      
      return newState;
    }

    default: {
      return state;
    }
  }
}
